// Time picker (hour + 15-min minute), replacing native <input type="time">.
//
// Rationale: on iOS Safari and inside modal/scroll containers, the native
// time picker's wheel or dropdown overlaps neighbouring controls and is
// nearly impossible to fully close. Switching to two shadcn <Select>s
// gives a deterministic, keyboard-friendly and iOS-safe control that
// snaps to the 15-min slot boundary the tracker uses everywhere else.
import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = ["00", "15", "30", "45"];

const pad = (n) => String(n).padStart(2, "0");
const label12 = (h) => {
  const p = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh} ${p}`;
};

/**
 * Controlled 15-min-aligned time picker.
 *
 * Props:
 *   value    "HH:MM"  (24-hour)  — required
 *   onChange (v) => void
 *   testIdPrefix  string used to build the hour/minute test-ids
 *   compact  boolean — when true, uses narrower trigger widths so the picker
 *            fits on very narrow mobile viewports (e.g. iPhone SE, 375px).
 */
export function TimePicker15({ value, onChange, testIdPrefix, compact = false }) {
  const [h, mRaw] = (value || "00:00").split(":");
  const hh = pad(Number(h) || 0);
  // Snap displayed minute to the nearest 15-min boundary so the UI never
  // shows a "MM=07" when the underlying value came from a legacy record.
  const nearest = MINUTES.reduce((best, m) =>
    Math.abs(Number(m) - Number(mRaw || 0)) <
    Math.abs(Number(best) - Number(mRaw || 0))
      ? m
      : best,
  MINUTES[0]);
  const mm = MINUTES.includes(mRaw) ? mRaw : nearest;

  const setHour = (nextH) => onChange(`${pad(Number(nextH))}:${mm}`);
  const setMinute = (nextM) => onChange(`${hh}:${nextM}`);

  const hourW = compact ? 66 : 74;
  const minW = compact ? 48 : 56;
  const trigCls = compact
    ? "h-10 bg-white px-1 gap-0 [&_svg]:h-3 [&_svg]:w-3 [&>span:first-child]:flex-1 [&>span:first-child]:text-center"
    : "h-10 bg-white px-1.5 gap-1 [&_svg]:h-3 [&_svg]:w-3 [&>span:first-child]:flex-1 [&>span:first-child]:text-center";

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 3, minWidth: 0 }}>
      <Select value={hh} onValueChange={setHour}>
        <SelectTrigger
          data-testid={testIdPrefix ? `${testIdPrefix}-hour` : undefined}
          className={trigCls}
          style={{ width: hourW, flexShrink: 0 }}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-64">
          {HOURS.map((n) => (
            <SelectItem key={n} value={pad(n)}>
              {label12(n)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span style={{ color: "#9AA6A0", fontWeight: 700, flexShrink: 0 }}>:</span>
      <Select value={mm} onValueChange={setMinute}>
        <SelectTrigger
          data-testid={testIdPrefix ? `${testIdPrefix}-minute` : undefined}
          className={trigCls}
          style={{ width: minW, flexShrink: 0 }}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MINUTES.map((m) => (
            <SelectItem key={m} value={m}>
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
